<template>
  <div class="cert-page">
    <div class="breadcrumb">
      <span class="breadcrumb-item" @click="goProfile">个人中心</span>
      <span class="breadcrumb-separator"> &gt; </span>
      <span class="breadcrumb-item" @click="goProfileInfo">个人资料</span>
      <span class="breadcrumb-separator"> &gt; </span>
      <span class="breadcrumb-item active">供应商认证</span>
    </div>

    <div class="con-box">
      <div class="page-title">供应商认证</div>
      <p class="page-tip">提交企业资料申请供应商认证，审核通过后将获得供应商权限。</p>

      <div class="form-item">
        <label class="form-label">企业名称</label>
        <el-input v-model="supplierCert.company_name" placeholder="请输入企业名称" style="max-width: 560px;" />
      </div>
      <div class="form-item">
        <label class="form-label">企业经营范围</label>
        <el-input v-model="supplierCert.business_scope" type="textarea" :rows="3" placeholder="请输入企业经营范围" style="max-width: 560px;" />
      </div>
      <div class="form-item">
        <label class="form-label">身份证正面</label>
        <div class="cert-uploads">
          <div class="cert-thumb" @click="$refs.supFront.click()">
            <img v-if="preview.id_card_front" :src="preview.id_card_front" alt="正面">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="supFront" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'id_card_front')">
          </div>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label">身份证反面</label>
        <div class="cert-uploads">
          <div class="cert-thumb" @click="$refs.supBack.click()">
            <img v-if="preview.id_card_back" :src="preview.id_card_back" alt="反面">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="supBack" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'id_card_back')">
          </div>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label">营业执照</label>
        <div class="cert-uploads">
          <div class="cert-thumb cert-thumb-wide" @click="$refs.supBiz.click()">
            <img v-if="preview.business_license" :src="preview.business_license" alt="营业执照">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="supBiz" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'business_license')">
          </div>
        </div>
      </div>

      <el-button type="primary" class="submit-btn" :loading="submitting" @click="submit">提交供应商认证</el-button>
    </div>
  </div>
</template>

<script>
import { getProfile, uploadImage, certifySupplier } from '@/api/user'

export default {
  name: 'SupplierCertification',
  data() {
    return {
      supplierCert: {
        id_card_front: '',
        id_card_back: '',
        business_license: '',
        company_name: '',
        business_scope: ''
      },
      preview: {
        id_card_front: '',
        id_card_back: '',
        business_license: ''
      },
      submitting: false
    }
  },
  async created() {
    await this.ensureRole()
  },
  beforeDestroy() {
    this.revokeAllBlobs()
  },
  methods: {
    goProfile() {
      this.$router.push('/profile')
    },
    goProfileInfo() {
      this.$router.push({ path: '/profile/index', query: { activeMenu: '1' }})
    },
    async ensureRole() {
      try {
        const res = await getProfile()
        if (res && res.code === 1 && res.data && Number(res.data.group_id) === 3) {
          this.$message.info('您已是供应商')
          this.$router.replace({ path: '/profile/index', query: { activeMenu: '1' }})
        }
      } catch (e) {
        console.error(e)
      }
    },
    revokeBlob(url) {
      if (url && String(url).startsWith('blob:')) {
        try { URL.revokeObjectURL(url) } catch (e) { /* ignore */ }
      }
    },
    revokeAllBlobs() {
      ['id_card_front', 'id_card_back', 'business_license'].forEach(k => {
        this.revokeBlob(this.preview[k])
      })
    },
    async onImage(e, fieldName) {
      const input = e.target
      const file = input.files && input.files[0]
      input.value = ''
      if (!file) return
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('只支持 JPG、JPEG、PNG 格式的图片')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }
      try {
        const response = await uploadImage(file)
        if (response && response.code === 1) {
          const url = response.data.url || response.data.fullurl || response.data
          this.supplierCert[fieldName] = typeof url === 'string' ? url : (url && url.url) || ''
          this.revokeBlob(this.preview[fieldName])
          this.preview[fieldName] = URL.createObjectURL(file)
          this.$message.success('上传成功')
        } else {
          this.$message.error(response?.msg || '上传失败')
        }
      } catch (err) {
        console.error(err)
        this.$message.error('上传失败，请重试')
      }
    },
    async submit() {
      const s = this.supplierCert
      if (!s.company_name.trim() || !s.business_scope.trim()) {
        this.$message.warning('请填写企业名称与经营范围')
        return
      }
      if (!s.id_card_front || !s.id_card_back || !s.business_license) {
        this.$message.warning('请上传身份证正反面与营业执照')
        return
      }
      this.submitting = true
      try {
        const res = await certifySupplier({
          id_card_front: s.id_card_front,
          id_card_back: s.id_card_back,
          business_license: s.business_license,
          company_name: s.company_name.trim(),
          business_scope: s.business_scope.trim()
        })
        if (res && res.code === 1) {
          this.$message.success(res.msg || '提交成功，请等待审核')
          this.$router.replace({ path: '/profile/index', query: { activeMenu: '1' }})
        } else {
          this.$message.error(res?.msg || '提交失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('提交失败，请重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.cert-page {
  width: var(--page-inner-ratio, 80%);
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
  margin: 0 auto;
  box-sizing: border-box;
}

.breadcrumb {
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
}

.breadcrumb-item {
  cursor: pointer;
  color: #1e80ff;
}

.breadcrumb-item:hover {
  text-decoration: underline;
}

.breadcrumb-item.active {
  color: #666;
  cursor: default;
}

.breadcrumb-item.active:hover {
  text-decoration: none;
}

.breadcrumb-separator {
  margin: 0 8px;
  color: #999;
}

.con-box {
  background: #fff;
  border-radius: 12px;
  padding: 24px 28px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.page-tip {
  font-size: 14px;
  color: #909399;
  line-height: 1.6;
  margin-bottom: 24px;
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.cert-uploads {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cert-thumb {
  width: 120px;
  height: 120px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  background: #fafafa;
  transition: border-color 0.2s ease;
}

.cert-thumb:hover {
  border-color: #1e80ff;
}

.cert-thumb-wide {
  width: 200px;
  min-height: 120px;
}

.cert-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.cert-thumb-ph {
  font-size: 12px;
  color: #909399;
  padding: 8px;
  text-align: center;
  line-height: 1.4;
}

.cert-file {
  display: none;
}

.submit-btn {
  margin-top: 8px;
}
</style>
